import React from "react";
import { KeyTerm, ImportantNote, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const psychologyUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Mental and Physical Health",
  examWeight: "15–25%",
  focus: "How biological, social, and psychological factors influence health and illness. The biopsychosocial model and evidence-based treatments.",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Introduction to Health Psychology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Health Psychology Overview</h3>
            <KeyTerm 
              term="Health Psychology" 
              definition="Focuses on how biological, social, and psychological factors influence health and illness. It moves away from the traditional biomedical model to a biopsychosocial model."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Stress and Stressors</h3>
            <KeyTerm 
              term="Stress" 
              definition="A process by which we perceive and respond to certain events (stressors) that we appraise as threatening or challenging."
            />
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3 mt-4">Types of Stressors:</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">Catastrophes</h5>
                <p className="text-sm text-slate-700 mt-1">Unpredictable, large-scale events (e.g., wars, earthquakes) that cause significant damage to emotional and physical health.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Significant Life Changes</h5>
                <p className="text-sm text-slate-700 mt-1">Transitions (e.g., leaving for college, death of a loved one, marriage). These are most stressful during young adulthood.</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h5 className="font-bold text-yellow-800">Daily Hassles</h5>
                <p className="text-sm text-slate-700 mt-1">Everyday irritations (e.g., traffic, long lines, work email). These are significant predictors of health issues due to their cumulative effect.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">Adverse Childhood Experiences (ACEs)</h5>
                <p className="text-sm text-slate-700 mt-1">Traumatic events occurring before age 18 (e.g., abuse, neglect). High ACE scores correlate with chronic health issues (heart disease, cancer) later in life.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Appraisal Theory (Lazarus)</h4>
            <p className="text-slate-700 leading-relaxed mb-4">
              Stress arises less from the event itself and more from how we appraise it.
            </p>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Primary Appraisal" 
                definition="Is this a threat?"
              />
              <KeyTerm 
                term="Secondary Appraisal" 
                definition="Do I have the resources to cope with it?"
              />
              <KeyTerm 
                term="Eustress" 
                definition="'Good' stress that motivates (e.g., the nerves before a big game)."
              />
              <KeyTerm 
                term="Distress" 
                definition="'Bad' stress that is debilitating."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Physiological Responses to Stress</h3>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">General Adaptation Syndrome (GAS) - Hans Selye</h4>
            <p className="text-slate-700 leading-relaxed mb-4">
              The body's adaptive response to stress in three phases:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h5 className="font-semibold text-red-800">Alarm Reaction</h5>
                  <p className="text-sm text-slate-700 mt-1">Sympathetic nervous system activation. Heart rate zooms, blood is diverted to skeletal muscles. The "Fight-or-Flight" response.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <span className="bg-yellow-200 text-yellow-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h5 className="font-semibold text-yellow-800">Resistance</h5>
                  <p className="text-sm text-slate-700 mt-1">Temperature, blood pressure, and respiration remain high. Adrenal glands pump hormones (cortisol, epinephrine) into the bloodstream. You are fully engaged.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h5 className="font-semibold text-blue-800">Exhaustion</h5>
                  <p className="text-sm text-slate-700 mt-1">With no relief from stress, the body's reserves run out. You become more vulnerable to illness/disease.</p>
                </div>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">The Stress-Disease Link</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">❤️ Heart Disease</h5>
                <p className="text-sm text-slate-700 mt-1">Chronic stress leads to inflammation and high blood pressure (hypertension), leading to coronary heart disease. Type A personalities (competitive, impatient, anger-prone) are more susceptible than Type B (easygoing).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">🛡️ Immune Suppression</h5>
                <p className="text-sm text-slate-700 mt-1">Stress energy diverts energy from the immune system. Stress does not <em>cause</em> AIDS or cancer, but it may speed up the progression by reducing the body's ability to fight off viruses and cancer cells.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Coping Mechanisms</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">Problem-Focused Coping</h5>
                <p className="text-sm text-slate-700 mt-1">Attempting to alleviate stress directly by changing the stressor or the way we interact with that stressor (e.g., studying for the test to remove the fear of failing). Used when we feel we have control.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Emotion-Focused Coping</h5>
                <p className="text-sm text-slate-700 mt-1">Attempting to alleviate stress by avoiding or ignoring a stressor and attending to emotional needs related to one's stress reaction (e.g., eating comfort food, venting to a friend). Used when we believe we cannot change the situation.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">Tend-and-Befriend Theory (Shelley Taylor)</h5>
                <p className="text-sm text-slate-700 mt-1">Under stress, people (especially women) often provide support to others (tend) and bond with and seek support from others (befriend). This is mediated by the hormone <strong>oxytocin</strong>.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Positive Psychology",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Positive Psychology Overview</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Positive Psychology</strong> is the scientific study of human flourishing, created largely by <strong>Martin Seligman</strong> as a counter to the field's historic focus on pathology (illness).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Core Concepts</h3>
            <KeyTerm 
              term="Subjective Well-Being" 
              definition="Self-perceived happiness or satisfaction with life. Used along with objective measures (physical and economic indicators) to evaluate people's quality of life."
            />
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3 mt-4">Three Pillars:</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">1. Positive Well-being</h5>
                <p className="text-sm text-slate-700 mt-1">Satisfaction with the past, happiness with the present, and optimism about the future.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">2. Positive Character</h5>
                <p className="text-sm text-slate-700 mt-1">Exploring key virtues (creativity, courage, compassion, integrity, self-control, leadership, wisdom).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">3. Positive Groups/Communities</h5>
                <p className="text-sm text-slate-700 mt-1">Healthy families, communal neighborhoods, effective schools.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Resilience and Growth</h3>
            <KeyTerm 
              term="Resilience" 
              definition="The personal strength that helps most people cope with stress and recover from adversity and even trauma."
            />
            <KeyTerm 
              term="Posttraumatic Growth (PTG)" 
              definition="Positive psychological changes as a result of struggling with extremely challenging circumstances and life crises. Instead of just 'bouncing back' (resilience), the individual 'bounces forward' to a higher level of functioning (e.g., increased appreciation for life, more meaningful relationships)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Evidence-Based Interventions</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h5 className="font-bold text-amber-800">Gratitude</h5>
                <p className="text-sm text-slate-700 mt-1">Research shows that listing 3 things you are grateful for daily increases subjective well-being and reduces depression scores.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h5 className="font-bold text-indigo-800">Signature Strengths</h5>
                <p className="text-sm text-slate-700 mt-1">Identifying and using one's top character strengths (e.g., wisdom, courage, humanity, justice, temperance, transcendence) leads to "flow" states and higher satisfaction.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Explaining and Classifying Psychological Disorders",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Defining a Disorder</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To be classified as a psychological disorder, behavior generally needs to meet the criteria of the <strong>"3 Ds"</strong> (often 4):
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Dysfunction</h4>
                  <p className="text-sm text-slate-600 mt-1">It interferes with daily life (school, work, relationships).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Distress</h4>
                  <p className="text-sm text-slate-600 mt-1">It causes the individual (or others) significant pain or upset.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Deviance</h4>
                  <p className="text-sm text-slate-600 mt-1">It differs significantly from societal norms (Note: This is highly culturally dependent).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-red-800">Danger</h4>
                  <p className="text-sm text-slate-700 mt-1">It poses a threat to self or others.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. The Biopsychosocial Approach</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Biological</h5>
                <p className="text-sm text-slate-700 mt-1">Evolution, individual genes, brain structure, and chemistry.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">Psychological</h5>
                <p className="text-sm text-slate-700 mt-1">Stress, trauma, learned helplessness, mood-related perceptions and memories.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">Social-Cultural</h5>
                <p className="text-sm text-slate-700 mt-1">Roles, expectations, definitions of "normality" and "disorder."</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Diathesis-Stress Model</h4>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <p className="text-sm text-slate-700 mb-2"><strong>Diathesis:</strong> A predisposition (genetic vulnerability).</p>
              <p className="text-sm text-slate-700 mb-2"><strong>Stress:</strong> Environmental triggers (trauma, poverty).</p>
              <p className="text-sm text-slate-700"><strong>Result:</strong> The disorder only manifests if both the predisposition and the stressor are present.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Classification Systems</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">DSM-5-TR</h5>
                <p className="text-sm text-slate-700 mt-1"><strong>Diagnostic and Statistical Manual of Mental Disorders, Text Revision</strong> - Published by the APA (American Psychiatric Association). It is the standard in the USA.</p>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-green-700"><strong>✓ Pros:</strong> Provides a common language for professionals; required for insurance billing.</p>
                  <p className="text-xs text-red-700"><strong>✗ Cons:</strong> Critics argue it "medicalizes" normal life behaviors; risk of overdiagnosis; labels can create stigma (Rosenhan study).</p>
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">ICD</h5>
                <p className="text-sm text-slate-700 mt-1"><strong>International Classification of Diseases</strong> - Published by the WHO. Used globally for all health statistics, not just mental health.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Selection of Categories of Psychological Disorders",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Neurodevelopmental Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Onset occurs during the developmental period (childhood).
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Autism Spectrum Disorder (ASD)</h5>
                <p className="text-sm text-slate-700 mt-1">Deficiencies in communication and social interaction; rigidly fixated interests and repetitive behaviors.</p>
                <p className="text-xs text-blue-700 mt-2"><strong>Etiology:</strong> Strong genetic influence; abnormal brain development; <strong>not</strong> caused by vaccines.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">Attention-Deficit/Hyperactivity Disorder (ADHD)</h5>
                <p className="text-sm text-slate-700 mt-1">Extreme inattention, hyperactivity, and/or impulsivity.</p>
                <p className="text-xs text-purple-700 mt-2"><strong>Controversy:</strong> Is it a disorder or high energy? Rates of diagnosis have skyrocketed. Treatment involves stimulants (Adderall/Ritalin) which paradoxically calm the ADHD brain.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Schizophrenia Spectrum Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A psychotic disorder characterized by a "split" from reality (not split personality).
            </p>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Positive Symptoms (Presence of inappropriate behaviors):</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">Hallucinations</h5>
                <p className="text-sm text-slate-700 mt-1">False sensory experiences (usually auditory/hearing voices).</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Delusions</h5>
                <p className="text-sm text-slate-700 mt-1">False beliefs (e.g., <em>Delusions of Grandeur</em> - "I am Jesus"; <em>Delusions of Persecution</em> - "The CIA is following me").</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h5 className="font-bold text-yellow-800">Disorganized Speech</h5>
                <p className="text-sm text-slate-700 mt-1">"Word Salad."</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Negative Symptoms (Absence of appropriate behaviors):</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Flat Affect</h5>
                <p className="text-sm text-slate-700 mt-1">Emotionless state.</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h5 className="font-bold text-indigo-800">Catatonia</h5>
                <p className="text-sm text-slate-700 mt-1">Rigidity or repetitive, purposeless movements.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Etiology:</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Dopamine Hypothesis" 
                definition="Excess dopamine receptors linked to positive symptoms."
              />
              <KeyTerm 
                term="Brain Structure" 
                definition="Enlarged fluid-filled ventricles (shrinkage of brain tissue)."
              />
              <KeyTerm 
                term="Prenatal Factors" 
                definition="Maternal virus during mid-pregnancy (flu) correlates with higher risk."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Depressive Disorders</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Major Depressive Disorder (MDD)</h5>
                <p className="text-sm text-slate-700 mt-1">Two or more weeks with five or more symptoms, at least one of which must be either (1) depressed mood or (2) loss of interest or pleasure (anhedonia).</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h5 className="font-bold text-slate-800">Persistent Depressive Disorder (Dysthymia)</h5>
                <p className="text-sm text-slate-700 mt-1">Mildly depressed mood more often than not for at least two years.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Etiology:</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Biological</h5>
                <p className="text-sm text-slate-700 mt-1">Low levels of <strong>Serotonin</strong> and <strong>Norepinephrine</strong>. High heritability.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">Social-Cognitive</h5>
                <p className="text-sm text-slate-700 mt-1"><strong>Rumination</strong> (overthinking problems). <strong>Learned Helplessness</strong> (Seligman). <strong>Beck's Cognitive Triad</strong> (Negative thoughts about Self, World, Future).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Bipolar and Related Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Characterized by alternating between depression and mania.
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">Bipolar I</h5>
                <p className="text-sm text-slate-700 mt-1">Full manic episodes (hyperactive, wildly optimistic, dangerous risk-taking, little sleep) usually alternating with major depression.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Bipolar II</h5>
                <p className="text-sm text-slate-700 mt-1">Hypomanic episodes (less severe mania) and major depression.</p>
              </div>
              <KeyTerm 
                term="Etiology" 
                definition="High genetic heritability."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Anxiety Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Distressing, persistent anxiety or maladaptive behaviors that reduce anxiety.
            </p>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Generalized Anxiety Disorder (GAD)" 
                definition="Pathological worry. Person is continually tense, apprehensive, and in a state of autonomic nervous system arousal."
              />
              <KeyTerm 
                term="Panic Disorder" 
                definition="Unpredictable, minutes-long episodes of intense dread (panic attacks). Often leads to fear of the next attack."
              />
              <KeyTerm 
                term="Specific Phobia" 
                definition="Persistent, irrational fear and avoidance of a specific object/situation (e.g., Arachnophobia)."
              />
              <KeyTerm 
                term="Agoraphobia" 
                definition="Fear of situations where escape might be difficult (e.g., open spaces, crowds), often leading to housebound behavior."
              />
              <KeyTerm 
                term="Social Anxiety Disorder" 
                definition="Intense fear of social situations, leading to avoidance of such."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Cultural Syndromes:</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">Taijin kyofusho (Japan)</h5>
                <p className="text-sm text-slate-700 mt-1">Fear that one's appearance/odor is offensive to others.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Ataque de nervios (Latinx/Caribbean)</h5>
                <p className="text-sm text-slate-700 mt-1">Intense emotional upset, screaming, fainting.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">F. Obsessive-Compulsive and Related Disorders</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">OCD</h5>
                <p className="text-sm text-slate-700 mt-1"><strong>Obsessions:</strong> Unwanted, repetitive thoughts (e.g., concern with germs).</p>
                <p className="text-sm text-slate-700 mt-1"><strong>Compulsions:</strong> Actions/responses to the thoughts (e.g., excessive hand washing).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Hoarding Disorder</h5>
                <p className="text-sm text-slate-700 mt-1">Difficulty discarding possessions regardless of value.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">G. Trauma and Stressor-Related Disorders</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <h5 className="font-bold text-red-800">Posttraumatic Stress Disorder (PTSD)</h5>
              <p className="text-sm text-slate-700 mt-1">Characterized by haunting memories, nightmares, social withdrawal, jumpy anxiety (hypervigilance), and insomnia that lingers for four weeks or more after a traumatic experience.</p>
              <p className="text-xs text-red-700 mt-2"><strong>Sensitive Limbic System:</strong> Those with smaller amygdalas may be more susceptible.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">H. Dissociative Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Conscious awareness becomes separated (dissociated) from previous memories, thoughts, and feelings.
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h5 className="font-bold text-indigo-800">Dissociative Identity Disorder (DID)</h5>
                <p className="text-sm text-slate-700 mt-1">A person exhibits two or more distinct and alternating personalities. Formerly Multiple Personality Disorder.</p>
                <p className="text-xs text-indigo-700 mt-2"><strong>Controversy:</strong> Very rare outside North America. Critics argue it may be role-playing induced by therapists.</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                <h5 className="font-bold text-cyan-800">Dissociative Amnesia</h5>
                <p className="text-sm text-slate-700 mt-1">Loss of memory for personal information, either partial or complete. Can include <strong>Dissociative Fugue</strong> (traveling away from familiar surroundings with amnesia).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">I. Feeding and Eating Disorders</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">Anorexia Nervosa</h5>
                <p className="text-sm text-slate-700 mt-1">Maintains a starvation diet despite being significantly underweight. High distorted body image. Highest mortality rate of psychiatric disorders.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Bulimia Nervosa</h5>
                <p className="text-sm text-slate-700 mt-1">Binge-eating episodes followed by distress and inappropriate weight-loss behaviors (vomiting, laxatives, excessive exercise). Weight often fluctuates within or above normal ranges.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">J. Personality Disorders</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Inflexible and enduring behavior patterns that impair social functioning.
            </p>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Cluster A (Odd/Eccentric):</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Paranoid" 
                definition="Distrust/suspicion."
              />
              <KeyTerm 
                term="Schizoid" 
                definition="Detachment from social relationships; limited emotion."
              />
              <KeyTerm 
                term="Schizotypal" 
                definition="Acute discomfort in relationships; cognitive distortions; eccentricities."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Cluster B (Dramatic/Impulsive):</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Antisocial Personality Disorder (ASPD)" 
                definition="Lack of conscience for wrongdoing; impulsive; aggressive. (Sociopath/Psychopath traits). Low autonomic arousal."
              />
              <KeyTerm 
                term="Borderline Personality Disorder (BPD)" 
                definition="Instability in relationships, self-image, and mood. Fear of abandonment."
              />
              <KeyTerm 
                term="Histrionic" 
                definition="Excessive emotionality and attention-seeking."
              />
              <KeyTerm 
                term="Narcissistic" 
                definition="Grandiosity, need for admiration, lack of empathy."
              />
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Cluster C (Anxious/Fearful):</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Avoidant" 
                definition="Social inhibition, feelings of inadequacy, hypersensitivity to negative evaluation."
              />
              <KeyTerm 
                term="Dependent" 
                definition="Submissive and clinging behavior."
              />
              <KeyTerm 
                term="Obsessive-Compulsive Personality Disorder (OCPD)" 
                definition="Preoccupation with orderliness, perfectionism, and control (distinct from OCD anxiety)."
              />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Treatment of Psychological Disorders",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">A. Psychoanalytic and Psychodynamic Therapies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Goal:</strong> Bring repressed feelings into conscious awareness (Insight Therapy).
            </p>
            
            <h4 className="text-lg font-semibold text-slate-700 mb-3">Techniques (Freud):</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Free Association" 
                definition="Saying whatever comes to mind to bypass the 'resistance' of the ego."
              />
              <KeyTerm 
                term="Dream Analysis" 
                definition="Latent content (hidden meaning)."
              />
              <KeyTerm 
                term="Transference" 
                definition="Patient transfers emotions linked with other relationships (e.g., love or hatred for a parent) onto the therapist."
              />
            </div>

            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Modern Psychodynamic:</strong> Face-to-face, less time-intensive, focuses on current symptoms via themes across important relationships.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">B. Humanistic Therapies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Goal:</strong> Boost self-fulfillment by helping people grow in self-awareness and self-acceptance.
            </p>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Client-Centered Therapy (Carl Rogers):</h4>
            <div className="space-y-3 mb-4">
              <KeyTerm 
                term="Active Listening" 
                definition="Echoing, restating, and clarifying what the client says."
              />
              <KeyTerm 
                term="Unconditional Positive Regard" 
                definition="A caring, accepting, nonjudgmental attitude."
              />
              <KeyTerm 
                term="Non-directive" 
                definition="The client leads the discussion."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">C. Behavioral Therapies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Goal:</strong> Applies learning principles to eliminate unwanted behaviors (doubt that self-awareness heals).
            </p>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Classical Conditioning Techniques (Exposure Therapies):</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Systematic Desensitization (Wolpe)</h5>
                <p className="text-sm text-slate-700 mt-1">Associates a pleasant relaxed state with gradually increasing anxiety-triggering stimuli. Uses a <strong>fear hierarchy</strong>. Commonly used for phobias.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">Aversion Therapy</h5>
                <p className="text-sm text-slate-700 mt-1">Associates an unpleasant state (nausea) with an unwanted behavior (drinking alcohol).</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Operant Conditioning Techniques:</h4>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h5 className="font-bold text-green-800">Token Economy</h5>
              <p className="text-sm text-slate-700 mt-1">People earn a token of some sort for exhibiting a desired behavior and can later exchange the tokens for various privileges or treats. (Used in institutional settings/ASD).</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">D. Cognitive Therapies</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Goal:</strong> Teach people new, more adaptive ways of thinking. Based on the assumption that thoughts intervene between events and our emotional reactions.
            </p>

            <div className="space-y-3 mb-4">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h5 className="font-bold text-purple-800">Rational-Emotive Behavior Therapy (REBT) - Albert Ellis</h5>
                <p className="text-sm text-slate-700 mt-1">Confrontational. Challenges people's "illogical, self-defeating attitudes/assumptions."</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Cognitive Therapy - Aaron Beck</h5>
                <p className="text-sm text-slate-700 mt-1">Gentler. Helps patients reverse <strong>catastrophizing</strong> beliefs about themselves (specifically for depression).</p>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <h5 className="font-bold text-indigo-800">Cognitive-Behavioral Therapy (CBT)</h5>
                <p className="text-sm text-slate-700 mt-1">An integrative therapy that combines changing self-defeating thinking (cognitive) with changing behavior (behavioral). Highly effective for anxiety and depression.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">E. Biomedical Therapies</h3>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Psychopharmacology (Drug Therapy):</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h5 className="font-bold text-red-800">Antipsychotics (e.g., Thorazine)</h5>
                <p className="text-sm text-slate-700 mt-1">Treat schizophrenia (positive symptoms) by blocking <strong>Dopamine</strong>.</p>
                <p className="text-xs text-red-700 mt-2"><strong>Side Effect:</strong> <strong>Tardive Dyskinesia</strong> (involuntary movements of facial muscles/tongue).</p>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                <h5 className="font-bold text-yellow-800">Antianxiety (e.g., Xanax, Ativan)</h5>
                <p className="text-sm text-slate-700 mt-1">Depress central nervous system activity. Increase <strong>GABA</strong>.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h5 className="font-bold text-green-800">Antidepressants (e.g., Prozac, Zoloft)</h5>
                <p className="text-sm text-slate-700 mt-1">Treat depression and anxiety. SSRIs (Selective Serotonin Reuptake Inhibitors) block the reuptake of <strong>Serotonin</strong>, leaving more in the synapse.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h5 className="font-bold text-blue-800">Mood Stabilizers (e.g., Lithium)</h5>
                <p className="text-sm text-slate-700 mt-1">Used for Bipolar Disorder to level emotional highs and lows.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Brain Stimulation:</h4>
            <div className="space-y-3 mb-4">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h5 className="font-bold text-orange-800">Electroconvulsive Therapy (ECT)</h5>
                <p className="text-sm text-slate-700 mt-1">Brief electric current sent through the brain of an anesthetized patient. Effective for severe, treatment-resistant depression.</p>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h5 className="font-bold text-pink-800">rTMS (Repetitive Transcranial Magnetic Stimulation)</h5>
                <p className="text-sm text-slate-700 mt-1">Magnetic energy applied to the brain (usually left frontal lobe) to stimulate suppressed activity.</p>
              </div>
            </div>

            <h4 className="text-lg font-semibold text-slate-700 mb-3">Psychosurgery:</h4>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h5 className="font-bold text-slate-800">Lobotomy</h5>
              <p className="text-sm text-slate-700 mt-1">Historically used to calm uncontrollably emotional or violent patients. Cut the nerves connecting the frontal lobes to the emotion-controlling centers of the inner brain. Rarely used today.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">F. Group and Family Therapy</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Benefits:</h4>
              <ul className="list-disc pl-5 space-y-2 text-slate-700 mt-2">
                <li>Saves therapists' time and clients' money</li>
                <li>Offers a social laboratory for exploring social behaviors</li>
                <li>Shows clients others share the same problems</li>
              </ul>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
              <h5 className="font-bold text-purple-800">Family Therapy</h5>
              <p className="text-sm text-slate-700 mt-1">Treats the family as a system. Views an individual's unwanted behaviors as influenced by, or directed at, other family members.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">G. Ethics in Treatment</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              According to the APA ethics code, therapists must ensure:
            </p>
            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Informed Consent</h4>
                  <p className="text-sm text-slate-600 mt-1">Clients must know the risks/nature of therapy.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Confidentiality</h4>
                  <p className="text-sm text-slate-600 mt-1">What is said stays private (unless there is a threat to safety).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Boundaries</h4>
                  <p className="text-sm text-slate-600 mt-1">No sexual or inappropriate relationships with clients.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Cultural Humility</h4>
                  <p className="text-sm text-slate-600 mt-1">Understanding and respecting the cultural background of the client.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default psychologyUnit5Notes;